
// 🔐 USER-SCOPED THEME KEY
// IMPORTANT:
// Only trust CURRENT live auth identity.
// Prevent stale auth_user cache from bleeding themes
// across logout/login account switching.
const getUserThemeKey = (emailOverride?: string | null) => {
  try {
    const email = String(
      emailOverride ||
      localStorage.getItem("email") ||
      sessionStorage.getItem("email") ||
      ""
    ).toLowerCase().trim();

    return email
      ? `ava_theme:${email}`
      : "ava_theme";
  } catch {
    return "ava_theme";
  }
};
import "../components/NeonTaskbar.css";
import "./Dashboard.css";
import "../styles/Dashboard.css";
import React, { useState, useEffect, useContext } from "react";
// import removed: null not exported from authCache
import ChatBox from "../components/ChatBox";
import AvaFace from "../components/AvaFace";
import Checkers3D from "../components/Checkers3D";
import { LayoutGrid, Power, Trash2, User } from "lucide-react";
import MobileOrbMenu from "../components/MobileOrbMenu";
import ConnectionModeStatusBadge from "../components/ConnectionModeStatusBadge";
import {
  usePreferences,
} from "../context/PreferencesContext";
import {
  getAvaaioUiCopy,
} from "../utils/avaaioUiCopy";
import { getUnifiedToken } from "../utils/unifiedAuth";
import { ThemeContext } from "../context/ThemeContext";

const API_BASE = import.meta.env.VITE_API_BASE || "https://api.avaaio.com";

export default function Dashboard() {
  const { theme, themeColors, setTheme: setGlobalTheme } = useContext(ThemeContext);
  const { preferences } = usePreferences();
  const interfaceCopy = getAvaaioUiCopy(
    preferences.language
  );
  const [language, setLanguage] = useState("en-US");
  const [activeGame, setActiveGame] = useState<string | null>(null);

  const [avaSpeaking, setAvaSpeaking] = useState(false);
  const [mouthLevel, setMouthLevel] = useState(0);
  
  const [time, setTime] = useState("");
  const [taskbarVisible, setTaskbarVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [me, setMe] = useState<{ email: string; memory?: any; role?: string; subscription_status?: string } | null>(null);


  // === ava-plan-updated listener (LIVE PLAN SYNC) ===
  useEffect(() => {
    const refreshUser = async () => {
      try {
          const token = getUnifiedToken();

          const res = await fetch(`${API_BASE}/api/v1/auth/me`, {
            credentials: "include",
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });

          if (!res.ok) return;

          const payload = await res.json();
          const userData = (payload as any)?.user ?? payload;

          setMe((userData as any) ?? null);
      } catch (err) {
        console.warn("[Dashboard] live plan sync failed:", err);
      }
    };

    window.addEventListener("ava-plan-updated", refreshUser);
    window.addEventListener("ava-force-refresh", refreshUser);

    // live plan delayed cookie sync: login/reactivation cookies may settle after route render
    const syncTimerFast = window.setTimeout(refreshUser, 400);
    const syncTimerSlow = window.setTimeout(refreshUser, 2200);

    return () => {
      window.clearTimeout(syncTimerFast);
      window.clearTimeout(syncTimerSlow);
      window.removeEventListener("ava-plan-updated", refreshUser);
      window.removeEventListener("ava-force-refresh", refreshUser);
    };
  }, []);


  
    const isAdmin = ["admin", "owner", "superadmin"].includes(String(me?.role || "").toLowerCase());
    const isPro =
      (String(me?.role || "").toLowerCase() === "vip") ||
      (String(me?.role || "").toLowerCase() === "pro") ||
      (String(me?.subscription_status || "").toLowerCase() === "active");

  if (me?.role || me?.subscription_status) {
    console.log("[DashboardAuth]", {
      role: me?.role,
      subscription_status: me?.subscription_status,
      isPro,
      isAdmin
    });
  }
    const isFree = !!me && !isAdmin && !isPro;

  // === MOUTH ANIMATION LOOP ===
  useEffect(() => {
    if (!avaSpeaking) {
      setMouthLevel(0);
      return;
    }

    let raf: number;

    const animate = () => {
      // Stronger, more visible motion
      const now = Date.now();

      // Ava mouth motion v2: slightly faster, softer attack, less mechanical.
      const primary = Math.pow(Math.abs(Math.sin(now * 0.0082)), 1.35);
      const secondary = Math.pow(Math.abs(Math.sin(now * 0.0039 + 0.9)), 1.8);

      const level =
        0.16 +
        primary * 0.50 +
        secondary * 0.16;

      setMouthLevel(level);

      raf = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [avaSpeaking]);


  useEffect(() => {
    const handler = (e: any) => {
      setAvaSpeaking(!!e.detail?.speaking);
    };

    window.addEventListener("ava-speaking", handler);

    return () => {
      window.removeEventListener("ava-speaking", handler);
    };
  }, []);


  useEffect(() => {
    const id = setInterval(() => {
      setTime(
        new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    }, 1000);

    return () => clearInterval(id);
  }, []);

  function handleSwitchAccount() {
    try {
      sessionStorage.setItem("ava_logged_out", "1");
    } catch {}

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {}

    window.location.href = "/login?logged_out=1&switch=1";
  }

  // Apply theme class to body when theme changes
  useEffect(() => {
    

    

    return () => {
      
    };
  }, [theme]);


  // Load user + set theme from saved memory
  useEffect(() => {
      const token = getUnifiedToken();

      fetch(`${API_BASE}/api/v1/auth/me`, {
        credentials: "include",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then(async (res) => {
            if (!res.ok) {
              throw new Error(`auth/me failed: ${res.status}`);
            }

            const payload = await res.json();
            const userData = (payload as any)?.user ?? payload;
            return userData;
          })
          .then((userData) => {
          if (!userData) return;
          setMe((userData as any) ?? null);
        const saved = ((userData as any).memory) || {};
        const role = String(((userData as any)?.role) || "").toLowerCase();
        const sub = String(((userData as any)?.subscription_status) || "").toLowerCase();
        const isAdmin = ["admin", "owner", "superadmin"].includes(role);
        const isPro =
          role === "vip" ||
          role === "pro" ||
          sub === "active";
        const scopedTheme = (() => {
          try {
            const key = getUserThemeKey(((userData as any)?.email));
            return (
              localStorage.getItem(key) ||
              localStorage.getItem(key + ":color") ||
              localStorage.getItem("ava_theme") ||
              localStorage.getItem("ava_theme_color")
            );
          } catch {
            return null;
          }
        })();

        // ThemeContext is the single theme authority.
        // Free accounts stay in low-power black mode.
        // Pro/Admin accounts keep the newest user-scoped selection.
        if (!isAdmin && !isPro) {
          setGlobalTheme("black" as any);
        } else if (scopedTheme && scopedTheme !== "black") {
          setGlobalTheme(scopedTheme as any);
        } else if (saved.theme_color && saved.theme_color !== "black") {
          setGlobalTheme(saved.theme_color as any);
        }
      })
      .catch(() => {});
  }, []);




  const requireProFeature = (featureName: string) => {
    if (isAdmin || isPro) return true;
    alert(
      interfaceCopy.dashboard.lockedFeature.replace(
        "{feature}",
        featureName
      )
    );
    return false;
  };

  const handleUserTyping = () => {};
  const handleGameCommand = (cmd: string) => {
    if (cmd === "checkers") setActiveGame("checkers");
  };
  const handleExit = () => setActiveGame(null);

  async function handleLogout() {
    if (
      typeof navigator !== "undefined" &&
      navigator.onLine === false
    ) {
      const confirmed = window.confirm(
        "You are currently offline. Logging out will end Local Mode access until you reconnect to the internet. Do you want to continue?"
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      sessionStorage.setItem("ava_logged_out", "1");
    } catch {}

    try {
      const keys = [
        "token",
        "access_token",
        "authToken",
        "jwt",
        "user",
        "auth_user",
        "profile",
        "ava_token",
        "ava_user",
        "profileDone"
      ];

      keys.forEach((k) => {
        try { localStorage.removeItem(k); } catch {}
        try {
          if (k !== "ava_logged_out") sessionStorage.removeItem(k);
        } catch {}
      });

      try {
        sessionStorage.setItem("ava_logged_out", "1");
      } catch {}
    } catch (e) {
      console.warn("Logout storage clear failed:", e);
    }

    try {
      await fetch("/api/v1/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.warn("Logout API failed:", e);
    }

    try {
      document.cookie.split(";").forEach((c) => {
        const cookie = c.replace(/^ +/, "");
        const eq = cookie.indexOf("=");
        const name = eq >= 0 ? cookie.slice(0, eq) : cookie;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.avaaio.com`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=sandbox.avaaio.com`;
      });
    } catch (e) {
      console.warn("Cookie clear failed:", e);
    }

    window.location.replace(`/login?logged_out=1&t=${Date.now()}`);
  }

  function handleDelete() {
    if (
      window.confirm(
        "Are you sure? This will permanently erase your account and all stored data."
      )
    ) {
      fetch("/api/auth/delete-account", {
        method: "DELETE",
        credentials: "include",
      })
        .then(() => {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = "/login?logged_out=1";
        })
        .catch((err) => {
          console.error("Delete account failed:", err);
        });
    }
  }

  function handleEditProfile() {
    window.location.href = "/setup";
  }



  const [compactViewport, setCompactViewport] = React.useState(false);

const [showAvaBoot, setShowAvaBoot] = React.useState(true);

React.useEffect(() => {
  if (!showAvaBoot) return;

  const timer = window.setTimeout(() => {
    try {
      sessionStorage.setItem("ava_boot_seen", "1");
    } catch {}

    setShowAvaBoot(false);
  }, 2500);

  return () => window.clearTimeout(timer);
}, [showAvaBoot]);


  // AVA_IPAD_LANDSCAPE_COLD_MOUNT_GATE
  // When an iPad refreshes directly in landscape, Safari may mount
  // WebGL before visualViewport and the dashboard have stable sizes.
  const [avaLandscapeMountReady, setAvaLandscapeMountReady] =
    React.useState(() => {
      if (
        typeof window === "undefined" ||
        typeof navigator === "undefined"
      ) {
        return true;
      }

      const isiPad =
        /iPad/.test(navigator.userAgent) ||
        (
          navigator.platform === "MacIntel" &&
          navigator.maxTouchPoints > 1
        );

      return !(isiPad && window.innerWidth > window.innerHeight);
    });

  React.useEffect(() => {
    if (avaLandscapeMountReady) return;

    let raf = 0;
    let fallbackTimer = 0;
    let previousSize = "";
    let stableFrames = 0;

    const checkLandscapeViewport = () => {
      const width = Math.round(
        window.visualViewport?.width || window.innerWidth
      );

      const height = Math.round(
        window.visualViewport?.height || window.innerHeight
      );

      const currentSize = `${width}x${height}`;

      if (
        width > height &&
        width > 0 &&
        height > 0 &&
        currentSize === previousSize
      ) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        previousSize = currentSize;
      }

      if (stableFrames >= 3) {
        window.dispatchEvent(new Event("resize"));
        setAvaLandscapeMountReady(true);
        return;
      }

      raf = window.requestAnimationFrame(checkLandscapeViewport);
    };

    raf = window.requestAnimationFrame(checkLandscapeViewport);

    fallbackTimer = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
      setAvaLandscapeMountReady(true);
    }, 1800);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(fallbackTimer);
    };
  }, [avaLandscapeMountReady]);

  React.useEffect(() => {
    const syncCompact = () => {
      try {
        const w = window.innerWidth;
        const h = window.innerHeight;
        const vv = window.visualViewport?.height || h;

        const landscape = w > h;

        const compactLandscape =
          landscape &&
          (
            h <= 430 ||
            (w <= 932 && h <= 480)
          );

        const keyboardOpen =
          vv < h - (compactLandscape ? 42 : 140);

        setCompactViewport(keyboardOpen);
      } catch {}
    };

    syncCompact();

    window.addEventListener("resize", syncCompact);
    window.addEventListener("orientationchange", syncCompact);
    window.visualViewport?.addEventListener("resize", syncCompact);

    return () => {
      window.removeEventListener("resize", syncCompact);
      window.removeEventListener("orientationchange", syncCompact);
      window.visualViewport?.removeEventListener("resize", syncCompact);
    };
  }, []);


  return (
    <div className="dashboard-container min-h-screen flex flex-col bg-black text-white overflow-visible">

      {showAvaBoot && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "radial-gradient(circle at center, #063b44 0%, #031116 48%, #000000 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <style>{`
            @keyframes avaPulse {
              0% { transform: scale(1); opacity:.85; }
              50% { transform: scale(1.06); opacity:1; }
              100% { transform: scale(1); opacity:.85; }
            }

            @keyframes avaLoadBar {
              0% { transform: scaleX(0); }
              100% { transform: scaleX(1); }
            }
          `}</style>

          <div
            style={{
              fontSize: "clamp(52px,7vw,96px)",
              fontWeight: 900,
              letterSpacing: "0.18em",
              color: "#24dcea",
              textShadow: "0 0 26px rgba(36,220,234,0.42)",
              animation: "avaPulse 2s ease-in-out infinite",
            }}
          >
            AVAAIO
          </div>

          <div
            style={{
              marginTop: 14,
              color: "#e7fbfd",
              fontSize: "14px",
              letterSpacing: "0.35em",
            }}
          >
            {interfaceCopy.dashboard.loadingSystem}
          </div>

          <div
            style={{
              width: 340,
              maxWidth: "80vw",
              height: 10,
              marginTop: 42,
              borderRadius: 999,
              background: "rgba(255,255,255,.08)",
              overflow: "hidden",
              boxShadow: "0 0 24px rgba(36,220,234,0.30)",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                transformOrigin: "left",
                animation: "avaLoadBar 2.2s linear forwards",
                background: "linear-gradient(90deg, #5ce3ed, #24dcea, #e7fbfd, #24dcea, #5ce3ed)",
              }}
            />
          </div>

          <div
            style={{
              marginTop: 22,
              color: "#24dcea",
              fontWeight: 700,
              letterSpacing: "0.25em",
            }}
          >
            {interfaceCopy.dashboard.loadingAva}
          </div>
        </div>
      )}
        {/* AVA plan banner hidden from dashboard UI. Real plan controls stay in Settings. */}
      <div
          className={`ava-live-stage flex-1 flex flex-col items-center ${
            compactViewport
              ? "justify-start pt-[max(calc(env(safe-area-inset-top)+135px),145px)]"
              : "justify-center"
          }`}
        >
        {!activeGame && (
          <>
            <AvaFace
              key={me?.email ?? "guest"}
              speaking={avaSpeaking}
              mouthLevel={mouthLevel}
              expression={"NEUTRAL"}
              
              isPro={!!isPro}
              isAdmin={!!isAdmin}
              avaEyeOpen={false}
                compactViewport={
                compactViewport &&
                window.innerWidth < 700
              }
            />
            <div className="ava-response-space" />
          </>
        )}
        {activeGame === "checkers" && <Checkers3D onExit={() => setActiveGame(null)} />}
      </div>

      <div className="chatbox-center-wrap chatbox-pos-bottom-center">
        <ChatBox
            
            
            
            
            
          />
      </div>

      <ConnectionModeStatusBadge />
      <MobileOrbMenu />

      {false && (
        <div className="taskbar desktop-tall">
          <div className="taskbar-inner flex justify-between items-center gap-3 px-3 overflow-x-auto">
            <div className="left-group flex items-center gap-3">
              <span className="clock text-lg">{time}</span>
            </div>

            <div className="flex flex-nowrap items-center gap-4">
              {/* Games */}
              <div
                className={`group relative ${menuOpen === "games" ? "open" : ""}`}
                onClick={() => {
                  if (!requireProFeature("Games")) return;
                  setMenuOpen(menuOpen === "games" ? null : "games");
                }}
              >
                <button className="menu-btn">🎮 Games</button>
                <div className="menu-dropdown">
                  <div onClick={() => { setActiveGame("checkers"); setMenuOpen(null); }}>Checkers</div>
                  <div onClick={() => { alert("Math flash cards coming soon!"); setMenuOpen(null); }}>Math Cards</div>
                  <div onClick={() => { alert("More games coming soon!"); setMenuOpen(null); }}>More coming soon</div>
                </div>
              </div>

              {/* Media */}
              <div
                className={`group relative ${menuOpen === "media" ? "open" : ""}`}
                onClick={() => {
                  if (!requireProFeature("Media")) return;
                  setMenuOpen(menuOpen === "media" ? null : "media");
                }}
              >
                <button className="menu-btn">🎬 Media & Entertainment</button>
                <div className="menu-dropdown">
                  <div onClick={() => { alert("Coming soon!"); setMenuOpen(null); }}>Coming soon</div>
                </div>
              </div>

              {/* Education */}
              <div
                className={`group relative ${menuOpen === "education" ? "open" : ""}`}
                onClick={() => {
                  if (!requireProFeature("Education")) return;
                  setMenuOpen(menuOpen === "education" ? null : "education");
                }}
              >
                <button className="menu-btn">🎓 Education</button>
                <div className="menu-dropdown">
                  <div onClick={() => { alert("Education mode coming soon"); setMenuOpen(null); }}>Coming soon</div>
                </div>
              </div>

              {/* Theme */}
              <div
                className={`group relative ${menuOpen === "theme" ? "open" : ""}`}
                onClick={() => {
                  if (!requireProFeature("Theme controls")) return;
                  setMenuOpen(menuOpen === "theme" ? null : "theme");
                }}
              >
                <button className="menu-btn">🎨 Theme</button>
                <div className="menu-dropdown">
                  {[
                      "cyan",
                      "purple",
                      "emerald",
                      "amber",
                      "lava",
                      "midnight",
                      "aurora",
                    ].map((c) => (
                    <div key={c} onClick={() => {
  setGlobalTheme(c as any);
  setMenuOpen(null);
}}>{c}</div>
                  ))}
                </div>
              </div>

              {/* Account */}
              <div
                className={`group relative ${menuOpen === "account" ? "open" : ""}`}
                onClick={() => setMenuOpen(menuOpen === "account" ? null : "account")}
              >
                <button className="menu-btn flex items-center">
                  <User size={16} className="mr-1" /> Account
                </button>
                <div className="menu-dropdown">
                  <div onClick={handleSwitchAccount}>Switch Account</div>
                  <div onClick={handleEditProfile}>Edit Profile</div>
                  <div className="text-red-400 hover:text-white" onClick={handleDelete}>Delete Account</div>
                </div>
              </div>
            </div>

            <div className="right-group flex items-center gap-3 relative">
              {me && (
                <span className="text-cyan-300 text-sm font-semibold mr-2">
                  {(me?.memory?.display_name || me?.email || 'User')}
                </span>
              )}

              {/* Hide Taskbar */}
              <button
                className="icon-btn multi"
                title="Hide Taskbar"
                onClick={() => setTaskbarVisible(false)}
              >
                <LayoutGrid size={22} />
              </button>

              {/* Power = Logout */}
              <button
                className="icon-btn multi"
                title="Logout"
                onClick={handleLogout}
              >
                <Power size={22} />
              </button>
            </div>
          </div>
        </div>
      )}

      {false && (
        <button
          className="fixed bottom-4 right-4 px-4 py-2 bg-[var(--color)] text-black font-bold rounded hover:opacity-90"
          onClick={() => setTaskbarVisible(true)}
        >
          Show Taskbar
        </button>
      )}
    </div>

  );
}


